import React from 'react';
import { FolderOpen, Star } from 'lucide-react';
export class FavoritesAggregator {
    /**
     * Get all favorites grouped by module - completely dynamic discovery
     */
    static getAllFavoritesByModule(userId) {
        const categories = [];
        const moduleGroups = this.getModulesWithFavorites(userId);
        for (const [moduleKey, favorites] of moduleGroups) {
            if (favorites.length > 0) {
                categories.push({
                    id: moduleKey,
                    title: this.getModuleTitle(moduleKey),
                    description: `${favorites.length} favorite${favorites.length !== 1 ? 's' : ''}`,
                    icon: React.createElement(FolderOpen, { className: 'w-4 h-4' }),
                    color: this.getModuleColorByIndex(Array.from(moduleGroups.keys()).indexOf(moduleKey)),
                    items: favorites
                });
            }
        }
        return categories;
    }
    /**
     * Get recent items grouped by module (time-based)
     */
    static getRecentItemsByModule() {
        const recentItems = this.getRecentItems();
        const moduleGroups = new Map();
        // Group recent items by module
        for (const item of recentItems) {
            const moduleItems = moduleGroups.get(item.moduleKey) || [];
            moduleItems.push(item);
            moduleGroups.set(item.moduleKey, moduleItems);
        }
        // Convert to categories
        const categories = [];
        const moduleKeys = Array.from(moduleGroups.keys());
        for (const [moduleKey, items] of moduleGroups) {
            if (items.length > 0) {
                // Sort by timestamp (most recent first) and visit count
                const sortedItems = items
                    .sort((a, b) => {
                    const timeDiff = b.timestamp - a.timestamp;
                    if (Math.abs(timeDiff) < 24 * 60 * 60 * 1000) { // Same day
                        return b.visitCount - a.visitCount; // Sort by visit count
                    }
                    return timeDiff; // Sort by time
                })
                    .slice(0, 10); // Limit to 10 items per module
                categories.push({
                    id: `${moduleKey}-recent`,
                    title: this.getModuleTitle(moduleKey),
                    description: `${sortedItems.length} recent item${sortedItems.length !== 1 ? 's' : ''}`,
                    icon: React.createElement(FolderOpen, { className: 'w-4 h-4' }),
                    color: this.getModuleColorByIndex(moduleKeys.indexOf(moduleKey)),
                    items: sortedItems.map(item => ({
                        label: item.label,
                        path: item.path,
                        description: item.description,
                        icon: item.icon,
                        count: item.visitCount,
                        badge: this.getTimeBadge(item.timestamp)
                    }))
                });
            }
        }
        return categories;
    }
    /**
     * Track a visited item for recent items
     */
    static trackRecentItem(path, label, description) {
        const moduleKey = this.inferModuleKeyFromPath(path);
        if (!moduleKey)
            return;
        const recentItems = this.getRecentItems();
        const existingIndex = recentItems.findIndex(item => item.path === path);
        const now = Date.now();
        if (existingIndex >= 0) {
            // Update existing item
            recentItems[existingIndex] = {
                ...recentItems[existingIndex],
                timestamp: now,
                visitCount: recentItems[existingIndex].visitCount + 1,
                label, // Update label in case it changed
                description
            };
        }
        else {
            // Add new item
            recentItems.unshift({
                label,
                path,
                description,
                timestamp: now,
                moduleKey,
                visitCount: 1
            });
        }
        // Clean up old items and limit size
        const cutoffDate = now - (this.RECENT_DAYS_THRESHOLD * 24 * 60 * 60 * 1000);
        const filteredItems = recentItems
            .filter(item => item.timestamp > cutoffDate)
            .slice(0, this.MAX_RECENT_ITEMS);
        this.saveRecentItems(filteredItems);
    }
    /**
     * Dynamically discover modules that have favorites in localStorage
     */
    static getModulesWithFavorites(userId) {
        const moduleGroups = new Map();
        const favoritesPrefix = 'textile-erp-favorites-';
        // Scan localStorage for favorites keys
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key?.startsWith(favoritesPrefix))
                continue;
            // Parse module key from storage key
            let moduleKey = key.replace(favoritesPrefix, '');
            // Handle user-specific keys (remove user ID suffix)
            if (userId && moduleKey.includes('-')) {
                const parts = moduleKey.split('-');
                if (parts[parts.length - 1] === userId) {
                    moduleKey = parts.slice(0, -1).join('-');
                }
                else {
                    continue; // Skip non-matching user IDs
                }
            }
            else if (!userId && moduleKey.includes('-')) {
                // Check if this is a user-specific key by seeing if last part looks like a user ID
                const parts = moduleKey.split('-');
                const lastPart = parts[parts.length - 1];
                if (lastPart.match(/^\d+$|^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/)) {
                    continue; // Skip user-specific keys when no userId provided
                }
            }
            const favorites = this.getFavoritesForModule(moduleKey, userId);
            if (favorites.length > 0) {
                moduleGroups.set(moduleKey, favorites);
            }
        }
        return moduleGroups;
    }
    /**
     * Get favorites for a specific module
     */
    static getFavoritesForModule(moduleKey, userId) {
        const storageKey = userId
            ? `textile-erp-favorites-${moduleKey}-${userId}`
            : `textile-erp-favorites-${moduleKey}`;
        try {
            const stored = localStorage.getItem(storageKey);
            if (!stored)
                return [];
            const favoritePaths = JSON.parse(stored);
            return favoritePaths.map(path => ({
                label: this.getLabelFromPath(path),
                path,
                description: this.getDescriptionFromPath(path, moduleKey),
                icon: React.createElement(Star, { className: 'w-3.5 h-3.5' })
            }));
        }
        catch (error) {
            console.error('Failed to load favorites for module:', moduleKey, error);
            return [];
        }
    }
    /**
     * Infer module key from path - completely dynamic based on existing data
     */
    static inferModuleKeyFromPath(path) {
        // First, check if we already have this path in any existing module's favorites
        const existingModules = this.getAllExistingModuleKeys();
        for (const moduleKey of existingModules) {
            const favorites = this.getFavoritesForModule(moduleKey);
            if (favorites.some(fav => fav.path === path)) {
                return moduleKey;
            }
        }
        // If not found in existing favorites, infer from path structure
        const segments = path.split('/').filter(Boolean);
        if (segments.length === 0)
            return null;
        // Use the first meaningful segment as module key
        const firstSegment = segments[0];
        // Convert to kebab-case module key format
        return this.pathSegmentToModuleKey(firstSegment);
    }
    /**
     * Get all existing module keys from localStorage
     */
    static getAllExistingModuleKeys() {
        const moduleKeys = new Set();
        const favoritesPrefix = 'textile-erp-favorites-';
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key?.startsWith(favoritesPrefix))
                continue;
            let moduleKey = key.replace(favoritesPrefix, '');
            // Remove user ID suffix if present
            const parts = moduleKey.split('-');
            const lastPart = parts[parts.length - 1];
            if (lastPart.match(/^\d+$|^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/)) {
                moduleKey = parts.slice(0, -1).join('-');
            }
            moduleKeys.add(moduleKey);
        }
        return Array.from(moduleKeys);
    }
    /**
     * Convert path segment to module key
     */
    static pathSegmentToModuleKey(segment) {
        // Convert to kebab-case if needed
        return segment.toLowerCase().replace(/[_\s]+/g, '-');
    }
    /**
     * Get recent items from localStorage
     */
    static getRecentItems() {
        try {
            const stored = localStorage.getItem(this.RECENT_ITEMS_KEY);
            return stored ? JSON.parse(stored) : [];
        }
        catch (error) {
            console.error('Failed to load recent items:', error);
            return [];
        }
    }
    /**
     * Save recent items to localStorage
     */
    static saveRecentItems(items) {
        try {
            localStorage.setItem(this.RECENT_ITEMS_KEY, JSON.stringify(items));
        }
        catch (error) {
            console.error('Failed to save recent items:', error);
        }
    }
    /**
     * Get human-readable module title from key
     */
    static getModuleTitle(moduleKey) {
        return moduleKey
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
    /**
     * Get module color by index (cycles through available colors)
     */
    static getModuleColorByIndex(index) {
        const colors = ['blue', 'green', 'purple', 'orange', 'red', 'indigo'];
        return colors[index % colors.length];
    }
    /**
     * Get time-based badge for recent items
     */
    static getTimeBadge(timestamp) {
        const now = Date.now();
        const diffHours = (now - timestamp) / (1000 * 60 * 60);
        const diffDays = diffHours / 24;
        if (diffHours < 1)
            return 'NOW';
        if (diffHours < 24)
            return 'TODAY';
        if (diffDays < 7)
            return `${Math.floor(diffDays)}d`;
        return undefined;
    }
    /**
     * Get label from path (smart parsing)
     */
    static getLabelFromPath(path) {
        const segments = path.split('/').filter(Boolean);
        if (segments.length === 0)
            return 'Unknown Item';
        const lastSegment = segments[segments.length - 1];
        // Handle common path patterns
        if (lastSegment === 'management') {
            return `${this.formatSegment(segments[segments.length - 2] || 'Item')} Management`;
        }
        return this.formatSegment(lastSegment);
    }
    /**
     * Get description from path
     */
    static getDescriptionFromPath(path, moduleKey) {
        const moduleTitle = this.getModuleTitle(moduleKey);
        return `Favorite from ${moduleTitle}`;
    }
    /**
     * Format segment to title case
     */
    static formatSegment(segment) {
        return segment
            .split(/[-_\s]+/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
    /**
     * Clear all recent items
     */
    static clearRecentItems() {
        localStorage.removeItem(this.RECENT_ITEMS_KEY);
    }
    /**
     * Migrate existing pinned items from sidebar to new favorites system
     */
    static migratePinnedItems(existingPinnedItems) {
        for (const path of existingPinnedItems) {
            const moduleKey = this.inferModuleKeyFromPath(path);
            if (!moduleKey)
                continue;
            // Add to module's favorites
            const storageKey = `textile-erp-favorites-${moduleKey}`;
            try {
                const existing = localStorage.getItem(storageKey);
                const favorites = existing ? JSON.parse(existing) : [];
                if (!favorites.includes(path)) {
                    favorites.push(path);
                    localStorage.setItem(storageKey, JSON.stringify(favorites));
                }
            }
            catch (error) {
                console.error('Failed to migrate pinned item:', path, error);
            }
        }
    }
}
FavoritesAggregator.RECENT_ITEMS_KEY = 'textile-erp-recent-items';
FavoritesAggregator.MAX_RECENT_ITEMS = 50;
FavoritesAggregator.RECENT_DAYS_THRESHOLD = 30;
